import {
    Directive,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { VGSCardInfo, VGSECheckInfo, VGSCollectForm, PaymentMethodData } from 'apps/public-portal/src/app/payment-integrations/base/models';
import { loadVGSCollect } from '@vgs/collect-js';
import { TellerOnlineErrorHandlerService } from 'teller-online-libraries/core';
import {
    TellerOnlineMessageService,
    TellerOnlineValidationService,
} from 'teller-online-libraries/shared';

/**
 * Shared base class for VGS Collect components (credit card and e-check).
 * Handles VGS library loading, form submit, field validation, and error extraction.
 * Subclasses implement type-specific form controls, VGS fields, and response mapping.
 */
@Directive()
export abstract class VgsCollectBase implements OnInit, OnDestroy {
    @Input() public formGroup: UntypedFormGroup;

    @Input() public paymentMethodData: PaymentMethodData;

    @Input() public vgsVaultId: string;

    @Input() public vgsEnvironment: string;

    @Input() public vgsVersion: string;

    @Input() public allowSavePaymentMethod: boolean;

    @Output() public loadingChange = new EventEmitter<boolean>();

    public loading = true;

    protected _vgsCollectForm: VGSCollectForm;

    protected _vgsPreviousFormState: any;

    protected _formChangeSubscription: Subscription;

    constructor(
        protected ngZone: NgZone,
        protected errorHandlerService: TellerOnlineErrorHandlerService,
        public messageService: TellerOnlineMessageService,
        public validationService: TellerOnlineValidationService,
    ) { }

    /** Subclass sets up its form controls (CC or echeck). */
    protected abstract initFormControls(): void;

    /** Subclass creates its VGS iframe fields. */
    protected abstract initVGSFields(): void;

    /** Subclass maps VGS submit response to paymentMethodData + form controls. */
    protected abstract mapVGSResponse(data: any): void;

    /** Subclass returns field mapping: { formControl: 'ccnumber', vgsField: 'cardNumber' } */
    protected abstract getValidationFieldMappings(): { formControl: string, vgsField: string, label: string }[];

    /** Subclass returns additional validation errors for its fields. */
    public abstract generateAdditionalErrors(): { [key: string]: string };

    ngOnInit(): void {
        this.initFormControls();
        loadVGSCollect({
            vaultId: this.vgsVaultId,
            environment: this.vgsEnvironment,
            version: this.vgsVersion,
        })
        .then((collect: any) => {
            this._vgsCollectForm = collect.init(formState =>
                this.handleVGSValidation(formState)
            );
            this.initVGSFields();
            this.setLoading(false);
        })
        .catch(error => {
            this.setLoading(false);
            this.messageService.notification('Unable to load payment form. An unexpected error occurred.', 'error', 5000);
            this.errorHandlerService.logErrorToServer(`Error loading VGS Collect: ${JSON.stringify(error)}`);
        });
    }

    ngOnDestroy(): void {
        this._formChangeSubscription?.unsubscribe();
    }

    public getFieldError(fieldName: string): string {
        return this.formGroup.controls[fieldName]?.value?.error ?? '';
    }

    public prepareValidation(): { [key: string]: string } {
        this.handleVGSValidation(this._vgsPreviousFormState, true);
        return this.generateAdditionalErrors();
    }

    public submitAndMapVGSFields(): Promise<VGSCardInfo | VGSECheckInfo> {
        return new Promise((resolve, reject) => {
            this._vgsCollectForm.submit(
                '/post',
                {},
                (status, data: any) => {
                    if (status >= 200 && status <= 300) {
                        this.mapVGSResponse(data);
                        resolve(data);
                    } else {
                        reject(new Error('An unexpected error occurred in VGS submit.'));
                    }
                },
                () => {
                    reject(new Error('Validation Errors in VGS submit.'));
                }
            );
        });
    }

    protected setLoading(value: boolean): void {
        this.loading = value;
        this.loadingChange.emit(value);
    }

    protected handleVGSValidation(formState: any, forceValidate = false): void {
        this._vgsPreviousFormState = formState;
        for (const mapping of this.getValidationFieldMappings()) {
            this.validateField(mapping.formControl, mapping.vgsField, mapping.label, formState, forceValidate);
        }
    }

    protected validateField(field: string, formStateField: string, label: string, formState: any, forceValidate = false): void {
        if (formState?.[formStateField]) {
            const control = this.formGroup.controls[field];
            if (control) {
                const invalid = !formState[formStateField].isValid && (formState[formStateField].isDirty || forceValidate);
                if (invalid) {
                    const errorMessage = formState[formStateField].errorMessages.join(' and ');
                    control.value.error = `${label} ${errorMessage}.`;
                } else if (formState[formStateField].isValid) {
                    control.value.error = null;
                }
                this.ngZone.run(() => {
                    this.formGroup.controls[field].setValue(control);
                });
            }
        }
    }
}
